import { isUndefined, Nullable } from '@silo/utils';
import { useSiloWeb3 } from '@silo/web3';
import { PriceProvidersRepository, SiloLens, SiloRepository, UniswapV3PriceProvider } from '@silo/typechain';
import {
  ChainIdLegacy,
  ChecksumAddress,
  ClientProvider,
  isSupportedChainId,
  ProtocolInstance,
  SigningProvider,
  SupportedChainId,
} from '@silo/types';
import {
  createPriceProvidersRepositoryContract,
  createSiloLensContract,
  createSiloRepositoryContract,
  createUniswapV3PriceProviderContract,
} from '@silo/contract-utils';
import { QueryFunction, useQuery } from '@tanstack/react-query';

type Web3ContextKey = [
  key: 'web3Context',
  chainId: ChainIdLegacy | undefined,
  protocolInstance: ProtocolInstance | undefined,
  clientProvider: ClientProvider | undefined,
  signingProvider: SigningProvider | undefined,
  account: ChecksumAddress | undefined
];

/**
 * @deprecated use web3 boundaries instead
 *
 * - `SiloWeb3ConnectionBoundary` + `useSiloWeb3Connection`
 * - `SiloWeb3UserBoundary` + `useSiloWeb3User`
 */
export interface Web3Context {
  user: Nullable<ChecksumAddress>;
  chainId: SupportedChainId;
  protocolInstance: ProtocolInstance;
  clientProvider: ClientProvider;
  signingProvider: SigningProvider;
  contracts: {
    siloLens: SiloLens;
    siloRepository: SiloRepository;
    priceProvidersRepository: PriceProvidersRepository;
    uniswapV3PriceProvider: UniswapV3PriceProvider;
  };
}

/**
 * @deprecated use web3 boundaries instead
 *
 * - `SiloWeb3ConnectionBoundary` + `useSiloWeb3Connection`
 * - `SiloWeb3UserBoundary` + `useSiloWeb3User`
 */
export const useWeb3Context = (): Web3Context | null => {
  const { account, clientProvider, signingProvider, chainId, protocolInstance } = useSiloWeb3();

  /**
   * While this data is not a query, here we are leveraging `useQuery` to
   * effectively create a global memoized value. No matter how many times this
   * hook is invoked, the web3Context will only be re-calculated when the
   * dependencies from useSiloWeb3 change.
   */
  const { data } = useQuery({
    queryKey: ['web3Context', chainId, protocolInstance, clientProvider, signingProvider, account],
    queryFn: getWeb3Context,
    networkMode: 'always',
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  return data ?? null;
};

const getWeb3Context: QueryFunction<Web3Context | null, Web3ContextKey> = ({
  queryKey: [, chainId, protocolInstance, clientProvider, signingProvider, account],
}) => {
  if (
    !clientProvider ||
    isUndefined(clientProvider?.network?.chainId) ||
    !signingProvider ||
    isUndefined(signingProvider?.network?.chainId) ||
    !isSupportedChainId(chainId) ||
    isUndefined(protocolInstance)
  ) {
    return null;
  }

  return {
    user: account,
    chainId,
    protocolInstance,
    clientProvider,
    signingProvider,
    contracts: {
      siloLens: createSiloLensContract({ protocolInstance, provider: clientProvider }),
      siloRepository: createSiloRepositoryContract({ protocolInstance, provider: clientProvider }),
      priceProvidersRepository: createPriceProvidersRepositoryContract({ protocolInstance, provider: clientProvider }),
      uniswapV3PriceProvider: createUniswapV3PriceProviderContract({ protocolInstance, provider: clientProvider }),
    },
  };
};
